import { useState } from "react";
import { MessagesSquare, Files, Users, Plus, FileCheck } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "./ui/button";
import BrandPane from "./BrandPane";
import NavigationPane from "./NavigationPane";
import ProjectsTable from "./ProjectsTable";
interface FlippedCards {
    [key: number]: boolean;
}

const HomePage = () => {
    const [hoveredStep, setHoveredStep] = useState<string | null>(null);
    const [flippedCards, setFlippedCards] = useState<FlippedCards>({});

    const cards = [
        {
            step: "Step 1",
            title: "Assets Upload",
            path: "/assets",
            icon: Files,
            description:
                "Upload your brand assets here. You can upload images and documents here.",
        },
        {
            step: "Step 2",
            title: "Persona Library",
            path: "/persona-library",
            icon: Users,
            description:
                "Explore and manage your persona collection. You can create new personas here.",
        },
        {
            step: "Step 3",
            title: "MLR Review",
            path: "/mlr",
            icon: FileCheck,
            description:
                "Complete the medical, legal, and regulatory review process",
        },
        {
            step: "Step 4",
            title: "Chat with Personas",
            path: "/chat-with-personas",
            icon: MessagesSquare,
            description:
                "Chat with AI personas of your reviewers to uncover the 'why' behind their responses and gain deeper insights.",
        },
    ];

    const toggleCard = (index: number) => {
        setFlippedCards((prev) => ({
            ...prev,
            [index]: !prev[index],
        }));
    };

    return (
        <div className="flex h-screen bg-gray-50">
            {/* Brand Pane */}
            <BrandPane />

            {/* Navigation Pane */}
            <NavigationPane />

            {/* Main content */}
            <div className="flex-1 overflow-y-auto h-screen">
                {/* Header */}
                <div className="p-6 flex justify-end bg-white">
                    <img
                        src="/synthio_logo_with_text.png"
                        alt="SynthioLabs Logo"
                        className="h-8 w-auto"
                    />
                </div>
                {/* Rest of the content */}
                <div className="p-6">
                    <h1 className="text-2xl font-bold text-gray-800 mb-8">
                        Welcome to SynthioLabs MLR Automation Platform,
                        Michelle!
                    </h1>

                    {/* Process Steps */}
                    <div className="mb-12">
                        <div className="relative w-full max-w-6xl mx-auto px-4">
                            <div className="absolute top-4 left-0 right-0 h-0.5 border-t-2 border-dotted border-[#5a55f4] z-0" />

                            <div className="flex justify-center gap-8 md:gap-16 lg:gap-24">
                                {cards.map((card, index) => (
                                    <div
                                        key={card.title}
                                        className="flex flex-col items-center z-10"
                                        onMouseEnter={() =>
                                            setHoveredStep(card.step)
                                        }
                                        onMouseLeave={() =>
                                            setHoveredStep(null)
                                        }
                                    >
                                        <div
                                            className={`px-4 py-2 rounded-full text-white flex items-center justify-center mb-4 transition-colors duration-300 text-sm font-medium`}
                                            style={{
                                                backgroundColor:
                                                    hoveredStep === card.step
                                                        ? "#22c55e"
                                                        : "#5a55f4",
                                            }}
                                        >
                                            {card.step}
                                        </div>
                                        <div
                                            className="relative w-72 h-72 cursor-pointer"
                                            onClick={() => toggleCard(index)}
                                        >
                                            <div
                                                className={`absolute w-full h-full transition-all duration-500 [transform-style:preserve-3d] ${
                                                    flippedCards[index]
                                                        ? "[transform:rotateY(180deg)]"
                                                        : ""
                                                }`}
                                            >
                                                {/* Front of card */}
                                                <Card className="absolute w-full h-full [backface-visibility:hidden]">
                                                    <CardContent className="h-full flex flex-col items-center justify-center p-6">
                                                        <card.icon
                                                            size={48}
                                                            className={`mb-6 transition-colors duration-300 ${
                                                                hoveredStep ===
                                                                card.step
                                                                    ? "text-green-600"
                                                                    : "text-[#5a55f4]"
                                                            }`}
                                                        />
                                                        <h3 className="text-xl font-semibold text-gray-800">
                                                            {card.title}
                                                        </h3>
                                                    </CardContent>
                                                </Card>

                                                {/* Back of card */}
                                                <Card className="absolute w-full h-full [backface-visibility:hidden] [transform:rotateY(180deg)]">
                                                    <CardContent className="h-full flex items-center justify-center p-6 text-center">
                                                        <p className="text-gray-600">
                                                            {card.description}
                                                        </p>
                                                    </CardContent>
                                                </Card>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Recent Activity Header */}
                        <div className="flex justify-between items-center mb-4 mt-16">
                            <h2 className="text-xl font-semibold text-gray-800">
                                Recent Activity
                            </h2>
                            <Button className="bg-[#5a55f4] hover:bg-[#0d0450] text-white flex items-center gap-2">
                                <Plus size={16} />
                                New Project
                            </Button>
                        </div>

                        {/* Activity Table */}
                        <ProjectsTable />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HomePage;
